import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { eventsApi, type ClassEventResponse } from '@/api/endpoints/events'
import { attachmentsApi } from '@/api/endpoints/attachments'
import { useUI } from '@/stores/ui'
import { toLocalDateTimeString } from '@/utils/date'

interface Props {
  open: boolean
  onClose: () => void
  event: ClassEventResponse | null
  onSaved: () => void
  onDeleted: () => void
}

const MAX_NOTES_WORDS = 1023
const MAX_ATTACHMENT_SIZE = 1024 * 1024 // 1MB in bytes

export default function EventEditModal({ open, onClose, event, onSaved, onDeleted }: Props) {
  const addToast = useUI((state) => state.addToast)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  const [topic, setTopic] = useState('')
  const [notes, setNotes] = useState('')
  const [eventDate, setEventDate] = useState('')
  const [attachments, setAttachments] = useState<Array<{ id: string; filename: string }>>([])
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showRecurringEditPrompt, setShowRecurringEditPrompt] = useState(false)

  useEffect(() => {
    if (open && event) {
      setTopic(event.topic || '')
      setNotes(event.notes || '')
      if (event.event_date) {
        // Convert stored datetime to datetime-local format, preserving the time.
        const date = new Date(event.event_date)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        setEventDate(`${year}-${month}-${day}T${hours}:${minutes}`)
      } else {
        setEventDate('')
      }
      // Load attachments from event
      if (event.attachment_ids && Array.isArray(event.attachment_ids) && event.attachment_ids.length > 0) {
        // attachment_ids are UUIDs, we need to load attachment details
        const ids = event.attachment_ids
        setAttachments(ids.map(id => ({ id, filename: 'Loading…' })))
        Promise.all(ids.map(id => attachmentsApi.get(id).catch(() => null))).then(rows =>
          setAttachments(ids.map((id, i) => ({ id, filename: rows[i]?.original_filename || rows[i]?.filename || 'Unavailable file' }))))
      } else {
        setAttachments([])
      }
      setShowDeleteConfirm(false)
    } else if (!open) {
      setTopic('')
      setNotes('')
      setEventDate('')
      setAttachments([])
      setShowDeleteConfirm(false)
    }
  }, [open, event])

  const wordCount = notes.trim().split(/\s+/).filter(w => w.length > 0).length
  const wordsRemaining = MAX_NOTES_WORDS - wordCount

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > MAX_ATTACHMENT_SIZE) {
      addToast({
        message: `File size must be less than 1MB. This file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`,
        type: 'error',
      })
      e.target.value = ''
      return
    }

    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'image/webp']
    if (!validTypes.includes(file.type)) {
      addToast({
        message: 'Only images (JPG, PNG, GIF, WebP) and PDFs are allowed.',
        type: 'error',
      })
      e.target.value = ''
      return
    }

    setUploading(true)
    try {
      const attachment = await attachmentsApi.upload(file)
      setAttachments(prev => [...prev, { id: attachment.id, filename: attachment.original_filename || file.name }])
      addToast({ message: 'File uploaded successfully', type: 'success' })
    } catch (error: any) {
      console.error('Failed to upload file:', error)
      addToast({
        message: error.response?.data?.detail || 'Failed to upload file',
        type: 'error',
      })
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const handleSave = async (editAll: boolean = false) => {
    if (!event || !topic.trim()) {
      addToast({ message: 'Topic is required', type: 'error' })
      return
    }

    if (wordCount > MAX_NOTES_WORDS) {
      addToast({ message: `Notes cannot exceed ${MAX_NOTES_WORDS} words`, type: 'error' })
      return
    }

    setSubmitting(true)
    try {
      const dateStr = eventDate ? toLocalDateTimeString(new Date(eventDate)) : undefined
      const payload = {
        topic: topic.trim(),
        notes: notes.trim() || null,
        event_date: dateStr || undefined,
        attachment_ids: attachments.map(a => a.id),
      }
      
      if (editAll && event.is_recurring) {
        // Update all events in the series
        await eventsApi.updateSeries(event.id, payload)
        addToast({ message: 'All events in series updated successfully', type: 'success' })
      } else {
        // Update single event
        await eventsApi.update(event.id, payload)
        addToast({ message: 'Event updated successfully', type: 'success' })
      }
      
      onSaved()
      onClose()
    } catch (error: any) {
      console.error('Failed to update event:', error)
      addToast({
        message: error.response?.data?.detail || 'Failed to update event',
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!event) return
    setDeleting(true)
    try {
      await eventsApi.remove(event.id)
      addToast({ message: 'Event deleted successfully', type: 'success' })
      onDeleted()
      onClose()
    } catch (error: any) {
      console.error('Failed to delete event:', error)
      addToast({
        message: error.response?.data?.detail || 'Failed to delete event',
        type: 'error',
      })
    } finally {
      setDeleting(false)
    }
  }

  if (!event) return null

  return (
    <AnimatePresence>
      {showRecurringEditPrompt && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-4">Edit Recurring Event</h3>
            <p className="text-sm text-gray-700 mb-6">
              This is a recurring event. Would you like to edit all events in this series, or just this event?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                className="btn-secondary focus-ring"
                onClick={() => {
                  setShowRecurringEditPrompt(false)
                  onClose()
                }}
              >
                Cancel
              </button>
              <button
                className="btn-primary focus-ring"
                onClick={() => {
                  setShowRecurringEditPrompt(false)
                  handleSave(false)
                }}
              >
                This Event Only
              </button>
              <button
                className="btn-primary focus-ring bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  setShowRecurringEditPrompt(false)
                  handleSave(true)
                }}
              >
                All Events in Series
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-modal="true"
          role="dialog"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose()
          }}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 hover-raise mx-4 max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Edit Event</h2>
                <p className="text-sm text-gray-500 mt-1">Update event details</p>
              </div>
              <button 
                className="btn-secondary text-sm focus-ring" 
                onClick={onClose}
                aria-label="Close edit modal"
              >
                ✕ Close
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Topic <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="input-field w-full focus-ring"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., Lesson 5: The Sacraments"
                  required
                  aria-required="true"
                  aria-label="Event topic"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Date & Time</label>
                <input
                  type="datetime-local"
                  step="900"
                  className="input-field w-full focus-ring"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  aria-label="Event date and time"
                />
                {eventDate && (
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(eventDate).toLocaleString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Notes <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    wordsRemaining < 0 
                      ? 'text-red-600 bg-red-50' 
                      : wordsRemaining < 50 
                      ? 'text-yellow-600 bg-yellow-50'
                      : 'text-gray-500 bg-gray-100'
                  }`}>
                    {wordCount}/{MAX_NOTES_WORDS} words
                  </span>
                </div>
                <textarea
                  className="input-field w-full h-32 resize-y focus-ring"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add reminders, homework assignments, special instructions, etc."
                  aria-label="Event notes"
                />
                {wordsRemaining < 0 && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <span>⚠️</span>
                    <span>Exceeds word limit. Please reduce to {MAX_NOTES_WORDS} words or less.</span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Attachments <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <label className="btn-secondary text-sm cursor-pointer focus-ring inline-flex items-center gap-2">
                      {uploading ? (
                        <>
                          <span className="animate-spin-smooth">⏳</span>
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <span>📎</span>
                          <span>Add Attachment</span>
                        </>
                      )}
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*,application/pdf"
                        onChange={handleFileUpload}
                        disabled={uploading}
                        aria-label="Upload attachment"
                      />
                    </label>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      Max 1MB • Images or PDFs
                    </span>
                  </div>
                  {attachments.length > 0 && (
                    <div className="space-y-2">
                      {attachments.map((att) => (
                        <div key={att.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-lg">📎</span>
                            <span className="text-sm text-gray-700 font-medium truncate">{att.filename}</span>
                          </div>
                          <button
                            className="text-xs text-red-600 hover:text-red-800 focus-ring px-2 py-1 rounded hover:bg-red-50 transition-colors"
                            onClick={() => setAttachments(prev => prev.filter(a => a.id !== att.id))}
                            aria-label={`Remove attachment: ${att.filename}`}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {showDeleteConfirm ? (
                <div className="p-5 bg-red-50 border-2 border-red-200 rounded-xl">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <p className="text-sm font-bold text-red-800 mb-1">Delete Event?</p>
                      <p className="text-sm text-red-700">
                        This will permanently delete this event. This action cannot be undone.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      className="btn-secondary text-sm focus-ring"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={deleting}
                      aria-label="Cancel deletion"
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-primary bg-red-600 hover:bg-red-700 text-sm focus-ring"
                      onClick={handleDelete}
                      disabled={deleting}
                      aria-label="Confirm delete event"
                    >
                      {deleting ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin-smooth">⏳</span>
                          <span>Deleting...</span>
                        </span>
                      ) : (
                        '🗑️ Delete Event'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between pt-5 border-t-2 border-gray-200">
                    <button
                      className="text-sm font-medium text-red-600 hover:text-red-800 focus-ring px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
                      onClick={() => setShowDeleteConfirm(true)}
                      title="Permanently delete this event. This action cannot be undone."
                      aria-label="Delete event"
                    >
                      🗑️ Delete Event
                    </button>
                  <div className="flex gap-3">
                    <button
                      className="btn-secondary focus-ring"
                      onClick={onClose}
                      disabled={submitting || deleting}
                      aria-label="Cancel editing"
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-primary focus-ring min-w-[140px]"
                      onClick={() => {
                        if (event.is_recurring) {
                          setShowRecurringEditPrompt(true)
                        } else {
                          handleSave(false)
                        }
                      }}
                      disabled={submitting || deleting || !topic.trim() || wordsRemaining < 0}
                      title="Save all changes to this event, including topic, date, notes, and attachments"
                      aria-label="Save changes"
                    >
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin-smooth">⏳</span>
                          <span>Saving...</span>
                        </span>
                      ) : (
                        '💾 Save Changes'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

